import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrapArray } from "../../lib/api-envelope";
import type { RentalOrderAssetRow } from "../../types/rental-period";
import { DASH, type InspectionType } from "./InspectionBits";
import type { InspectionDraft } from "../../pages/rentals/RentalInspectionDraft";

/**
 * New Inspection: picks the asset, the order and the type, then opens the
 * draft at /rentals/inspections/new. Nothing exists on the server until the
 * draft is submitted.
 *
 * Two doors. From the Inspections register every asset and order is offered.
 * From a dispatch's page (`fromDispatch`) the order is the dispatch's own and
 * is not offered as a choice, the assets are that order's, and the draft
 * carries `dispatch_id` (api#1068). The API refuses an asset that is not on
 * the dispatch's order with a 404, shown on the draft as sent.
 */

interface AssetOption {
  id: string;
  name: string;
  tag: string;
}

interface OrderOption {
  id: string;
  order_number: string;
  customer_name: string;
}

export interface InspectionDispatchContext {
  dispatch_id: string;
  dispatch_label: string;
  order_id: string;
  order_number: string;
  customer_name: string;
  /** A pickup inspects what comes back; anything else, what goes out. */
  type: InspectionType;
}

/** The Yard page's native select, so the dialog reads the same (#819). */
const nativeSelect =
  "h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

export function NewInspectionDialog({
  open,
  onOpenChange,
  fromDispatch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromDispatch?: InspectionDispatchContext;
}) {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [orders, setOrders] = useState<OrderOption[]>([]);
  const [assetId, setAssetId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [type, setType] = useState<InspectionType>(fromDispatch?.type ?? "pre_delivery");
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const fetchOptions = useCallback(async () => {
    setLoaded(false);
    try {
      if (fromDispatch) {
        const res = await api.rentals.orders.getAssets(fromDispatch.order_id);
        setAssets(
          unwrapArray<RentalOrderAssetRow>(res.data).map((a) => ({ id: a.asset_id, name: a.asset_name ?? DASH, tag: a.asset_number ?? "" })),
        );
        setOrders([]);
      } else {
        const [assetsRes, ordersRes] = await Promise.all([api.rentals.assets.list(), api.rentals.orders.list()]);
        setAssets(
          unwrapArray<{ id: string; name?: string | null; asset_number?: string | null }>(assetsRes.data).map((a) => ({
            id: a.id,
            name: a.name ?? DASH,
            tag: a.asset_number ?? "",
          })),
        );
        setOrders(unwrapArray<OrderOption>(ordersRes.data));
      }
      setOptionsError(null);
    } catch (err) {
      // The pickers are required, so a failed load is a dead dialog; say so.
      setAssets([]);
      setOrders([]);
      setOptionsError(getApiErrorMessage(err, fromDispatch ? "Could not load this order's assets." : "Could not load assets and orders."));
    } finally {
      setLoaded(true);
    }
  }, [fromDispatch]);

  useEffect(() => {
    if (!open) return;
    setAssetId("");
    setOrderId("");
    setType(fromDispatch?.type ?? "pre_delivery");
    fetchOptions();
  }, [open, fromDispatch, fetchOptions]);

  const create = () => {
    const pickedOrder = fromDispatch ? fromDispatch.order_id : orderId;
    if (!assetId || !pickedOrder) {
      toast.error(fromDispatch ? "Please select an asset." : "Please select both an asset and an order.");
      return;
    }
    const asset = assets.find((a) => a.id === assetId);
    const order = orders.find((o) => o.id === orderId);
    const draft: InspectionDraft = {
      asset_id: assetId,
      asset_name: asset?.name ?? DASH,
      asset_tag: asset?.tag || DASH,
      order_id: pickedOrder,
      order_number: fromDispatch ? fromDispatch.order_number : order?.order_number ?? DASH,
      customer_name: fromDispatch ? fromDispatch.customer_name : order?.customer_name ?? DASH,
      type,
      ...(fromDispatch ? { dispatch_id: fromDispatch.dispatch_id, dispatch_label: fromDispatch.dispatch_label } : {}),
    };
    onOpenChange(false);
    navigate("/rentals/inspections/new", { state: { draft } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Inspection</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Native and labelled, as on the Yard page (#819): ui/select prints the
              raw value in its trigger, so a picked asset read as its id and the
              type as pre_delivery. */}
          <div className="space-y-2">
            <Label htmlFor="new-inspection-asset" className="text-sm text-fieldiq-muted">Asset</Label>
            {assets.length > 0 ? (
              <select id="new-inspection-asset" value={assetId} onChange={(e) => setAssetId(e.target.value)} className={nativeSelect}>
                <option value="">Select asset...</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.tag ? `${a.name} (${a.tag})` : a.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-fieldiq-muted">
                {optionsError ?? (!loaded ? "Loading…" : fromDispatch ? "This order has no assets." : "No rental equipment yet.")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            {fromDispatch ? (
              <>
                <p className="text-sm text-fieldiq-muted">Order</p>
                <p className="text-sm text-fieldiq-text" data-testid="new-inspection-fixed-order">
                  {fromDispatch.order_number} — {fromDispatch.customer_name}
                  <span className="block text-xs text-fieldiq-muted">On dispatch {fromDispatch.dispatch_label}</span>
                </p>
              </>
            ) : (
              <>
                <Label htmlFor="new-inspection-order" className="text-sm text-fieldiq-muted">Order</Label>
                {orders.length > 0 ? (
                  <select id="new-inspection-order" value={orderId} onChange={(e) => setOrderId(e.target.value)} className={nativeSelect}>
                    <option value="">Select order...</option>
                    {orders.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.order_number} — {o.customer_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-xs text-fieldiq-muted">{optionsError ?? (!loaded ? "Loading…" : "No rental orders yet.")}</p>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-inspection-type" className="text-sm text-fieldiq-muted">Inspection Type</Label>
            <select id="new-inspection-type" value={type} onChange={(e) => setType(e.target.value as InspectionType)} className={nativeSelect}>
              <option value="pre_delivery">Pre-Delivery</option>
              <option value="post_return">Post-Return</option>
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-fieldiq-muted hover:text-fieldiq-text">
            Cancel
          </Button>
          <Button onClick={create} className="fi-action fi-action--standard fi-action--primary">
            Create Inspection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
